import { ActionKeys } from '~/actions/constants';
import { GTMEventPayload, sendGTMEvent } from '~/actions/gtm.actions';
import * as plansActions from '~/actions/plans.actions';
import { AxiosResponse } from 'axios';
import {
  CLOSE_ACCOUNT_MISSING_FEATURE,
  CLOSE_ACCOUNT_OTHER,
  CLOSE_ACCOUNT_PREFERRED_COMPETITOR,
} from '~/components/Select/options';
import { push } from 'connected-react-router';
import { USER_PLAN_PUSHED_EVENT } from '~/constants/gtm';
import { WIX_PRO_PLAN_NOTIFICATION_DISMISSED } from '~/constants/local-storage-keys';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getCurrentPlan } from '~/selectors/plans.selectors';
import * as plansService from '~/services/plans.service';
import * as settingsService from '~/services/settings.service';
import Plan, { fromJSON, UserPlan, userPlanFromJSON } from '~/types/Plan';
import { saveInLocalStorage } from '~/utils/local-storage';
import { planToTrackingDetails } from '~/utils/plans.util';
import { Events, track } from '~/utils/segment';
import { selectState } from '~/sagas/index';

export function* fetchUserPlans() {
  try {
    const response: AxiosResponse = yield call(plansService.getUserPlans);
    const userPlans: UserPlan[] = response.data.results.map(userPlanFromJSON);
    yield put(plansActions.fetchUserPlansSuccess(userPlans));
  } catch (e) {
    yield put(plansActions.fetchUserPlansError(e.toString()));
  }
}

export function* fetchPricingPlans() {
  try {
    const response: AxiosResponse = yield call(plansService.getPricingPlans);
    const pricingPlans: Plan[] = response.data.results.map(fromJSON);
    yield put(plansActions.fetchPricingPlansSuccess(pricingPlans));
  } catch (e) {
    yield put(plansActions.fetchPricingPlansError(e.toString()));
  }
}

export function* pushPlanToGTM() {
  const currentPlan: Maybe<UserPlan> = yield select(getCurrentPlan);
  const payload = {
    userIsPro: false,
  };

  if (currentPlan) {
    payload.userIsPro = currentPlan.plan.isPro || currentPlan.plan.isFullAccess;
  }

  const gtmEvent: GTMEventPayload = {
    eventName: USER_PLAN_PUSHED_EVENT,
    data: payload,
  };

  yield put(sendGTMEvent(gtmEvent));
}

export function* updateUserPlan(action: ReturnType<typeof plansActions.updateUserPlan>) {
  try {
    const currentPlan: Maybe<UserPlan> = yield selectState<Maybe<UserPlan>>((state) =>
      getCurrentPlan(state),
    );

    const response: AxiosResponse = yield call(plansService.setUserPlan, action.payload.planName);
    const userPlan: UserPlan = userPlanFromJSON(response.data);

    yield call(track, Events.plan_updated, {
      operation: 'started',
      ...planToTrackingDetails(userPlan),
      previous_plan_name: currentPlan?.plan?.displayName,
      entry_point: action.payload.entryPoint,
    });

    yield put(plansActions.updateUserPlanSuccess(userPlan));
  } catch (e) {
    yield put(plansActions.updateUserPlanError(e.toString()));
  }
}

export function* closeWixProPlanNotification() {
  yield call(saveInLocalStorage, WIX_PRO_PLAN_NOTIFICATION_DISMISSED, 'true');
}

export function* submitCloseAccountForm(
  action: ReturnType<typeof plansActions.submitCloseAccountForm>,
) {
  const { reason, tellUsMore } = action.payload;
  const tellUsMoreReasons = [
    CLOSE_ACCOUNT_OTHER.value,
    CLOSE_ACCOUNT_PREFERRED_COMPETITOR.value,
    CLOSE_ACCOUNT_MISSING_FEATURE.value,
  ];

  let payload = {
    churn_reason: reason,
  };

  if (tellUsMoreReasons.includes(reason)) {
    // special case handling for "other"
    if (reason === CLOSE_ACCOUNT_OTHER.value) {
      payload['other_reason'] = tellUsMore;
    } else {
      payload[reason] = tellUsMore;
    }
  }

  try {
    const response = yield call([settingsService, settingsService.closeAccount], payload);
    if (response?.data?.message) {
      const closeStatus =
        response?.data?.message === 'Account successfully closed.' ? 'full' : 'partial';
      yield put(plansActions.closeAccountSuccess(closeStatus));
    } else {
      yield put(plansActions.closeAccountError('account.plan.closeAccount.form.apiError'));
    }
  } catch (e) {
    yield put(plansActions.closeAccountError('account.plan.closeAccount.form.apiError'));
  }
}

export function* closeAccountSuccess(action: ReturnType<typeof plansActions.closeAccountSuccess>) {
  try {
    yield put(push(`/close?closeStatus=${action.payload.closeStatus}`));
  } catch (e) {
    yield put(plansActions.closeAccountError('account.plan.closeAccount.form.apiError'));
  }
}

export default [
  takeLatest(ActionKeys.FETCH_USER_PLANS, fetchUserPlans),
  takeLatest(ActionKeys.UPDATE_USER_PLAN_SUCCESS, fetchUserPlans),
  takeLatest(ActionKeys.FETCH_USER_PLANS_SUCCESS, pushPlanToGTM),
  takeLatest(ActionKeys.FETCH_PRICING_PLANS, fetchPricingPlans),
  takeLatest(ActionKeys.UPDATE_USER_PLAN, updateUserPlan),
  takeLatest(ActionKeys.CLOSE_WIX_PRO_PLAN_NOTIFICATION, closeWixProPlanNotification),
  takeLatest(ActionKeys.SUBMIT_CLOSE_ACCOUNT_FORM, submitCloseAccountForm),
  takeLatest(ActionKeys.CLOSE_ACCOUNT_SUCCESS, closeAccountSuccess),
];
